import { ref, provide, watch } from 'vue';
import { useCrud } from './useCrud';

/**
 * Composable for managing list views with CRUD operations
 * Replaces common patterns across List.vue components
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.api - API service instance from useApi()
 * @param {Component} options.formComponent - Vue component for add/edit modal
 * @param {string} options.displayField - Field name used for display in confirm messages (default: 'name')
 * @param {Object} options.route - Vue Router route object
 * @param {Object} options.router - Vue Router instance
 * @param {Function} options.getQueryParams - Optional function to get additional query params for API calls
 * @param {Function} options.onLoadError - Optional callback receiving the error when a page fails to load
 */
/**
 * Rows per page.
 *
 * Every page is a round trip, and what the reader feels at the bottom of a long
 * list is the number of round trips, not their size: a page of 25 buys about a
 * screen and a half of scrolling, a page of 50 buys three. `smartPaginate` caps
 * the server side at 50, so this is the largest page a list can ask for. Both
 * parameter names are sent because the endpoints are split between them, and an
 * endpoint that reads neither simply ignores both.
 */
const PAGE_SIZE = 50;

export function useList(options = {}) {
    const {
        api,
        formComponent,
        displayField = 'name',
        route,
        router,
        getQueryParams = () => ({}),
        withCount = false,
        pageSize = PAGE_SIZE,
        onLoadError = () => {}
    } = options;

    // Use CRUD composable for add/edit/remove operations
    const { rows, selected, addItem, editItem, removeItem, removeSelection } = useCrud({
        api,
        formComponent,
        displayField,
        route,
        router
    });

    // List-specific state
    const checked = ref([]);
    const checkedAll = ref(false);
    const page = ref(0);
    const lastPage = ref(false);
    const loading = ref(false);
    const loadingAll = ref(false);
    const total = ref(null);
    // Whatever the endpoint says about the result set beyond its rows — filled
    // from the first page's `meta` object, empty when it sends none.
    const meta = ref({});
    const infiniteLoadingId = ref(Date.now());
    // Timestamp of the last successful load, and whether a silent background
    // refresh is currently in flight (see refreshList).
    const lastLoadedAt = ref(null);
    const refreshing = ref(false);
    // Bumped by every resetList(). A request that started before the reset answers
    // with the *old* filters, and it can land after the fresh one: appending its
    // rows would show records the current filters exclude (and overwrite `total`
    // with a count that doesn't match them). Responses stamped with a superseded
    // generation are dropped instead.
    const generation = ref(0);

    /**
     * Reset list to initial state and trigger reload
     */
    function resetList() {
        generation.value++;
        loading.value = false;
        page.value = 0;
        lastPage.value = false;
        rows.value = [];
        checked.value = [];
        checkedAll.value = false;
        total.value = null;
        meta.value = {};
        lastLoadedAt.value = null;
        infiniteLoadingId.value = Date.now();
    }

    /**
     * Toggle all checkboxes
     */
    function toggleAll() {
        checked.value = [];
        if (!checkedAll.value) {
            for (const row of rows.value) {
                checked.value.push(row.id);
            }
            checkedAll.value = true;
        } else {
            checkedAll.value = false;
        }
    }

    /**
     * Remove all selected items (wrapper around useCrud's removeSelection)
     * @param {Object} options - Optional settings
     * @param {Object} options.listRef - Reference to menu list element (for selecting active if none checked)
     * @param {string} options.redirectRoute - Route name to redirect after delete
     */
    async function removeSelectionList(options = {}) {
        const { listRef, redirectRoute } = options;

        // If nothing checked, try to check the active item
        if (checked.value.length === 0) {
            if (listRef?.value) {
                const target = listRef.value.querySelector('a.is-active');
                if (target) {
                    const input = target.parentElement.querySelector('input');
                    input?.click();
                }
            }
            return;
        }

        // Call useCrud's removeSelection with onReset callback
        await removeSelection(checked.value, {
            redirectRoute,
            onReset: () => {
                resetList();
            }
        }).catch(() => {
            checked.value = [];
        });
    }

    // The page request of the current generation while it is in flight. Every
    // caller asking for the next page meanwhile — the loader re-armed by a reset,
    // a superseded request handing over, a grouping pulling every page — joins
    // it rather than sending its own: two requests for the same page both
    // append, and the list shows every row twice.
    let inFlight = null;

    /**
     * Fetch the next page and append it to rows.
     * Shared core for both infinite scroll and load-all-pages (grouping).
     * @returns {Promise<{ done: boolean, error: boolean }>} done once the last
     *          page is reached; error if the request failed (page not advanced)
     */
    function fetchNextPage() {
        if (inFlight && inFlight.generation === generation.value) {
            return inFlight.promise;
        }
        if (lastPage.value) {
            return Promise.resolve({ done: true, error: false });
        }

        const requestGeneration = generation.value;
        const promise = fetchPage(requestGeneration).finally(() => {
            if (inFlight?.promise === promise) {
                inFlight = null;
            }
        });
        inFlight = { generation: requestGeneration, promise };

        return promise;
    }

    async function fetchPage(requestGeneration) {
        loading.value = true;
        const nextPage = page.value + 1;

        try {
            const params = {
                page: nextPage,
                page_size: pageSize,
                per_page: pageSize,
                ...getQueryParams()
            };

            // Ask the backend for the authoritative row count only on the first
            // page: the total is filter-dependent but page-independent, so one
            // COUNT per filter set is enough (repeats are served by the query
            // cache). This flips smartPaginate() from simplePaginate to paginate.
            if (withCount && nextPage === 1) {
                params.with_count = true;
            }

            const response = await api.index(params);

            // The filters changed while this page was in flight: its rows belong to
            // a list that no longer exists. Drop them and answer the caller with the
            // page of the current filters — joined if it is already on its way, so
            // the loader that triggered this still gets its state resolved.
            if (requestGeneration !== generation.value) {
                return fetchNextPage();
            }

            const data = response.data.data || response.data;
            page.value = nextPage;

            if (response.data.total !== undefined) {
                total.value = response.data.total;
            }

            // The result set is described by the first page; later pages carry the
            // same meta and must not overwrite it once filters moved on.
            if (nextPage === 1) {
                meta.value = response.data.meta || {};
            }

            if (data.length > 0) {
                rows.value = [...rows.value, ...data];
                if (data.length < response.data.per_page) {
                    lastPage.value = true;
                }
            } else {
                lastPage.value = true;
            }

            lastLoadedAt.value = Date.now();
        } catch (error) {
            // Same for a failure: the filters moved on, so this error belongs to a
            // list nobody is looking at — retry under the current ones instead of
            // surfacing it.
            if (requestGeneration !== generation.value) {
                return fetchNextPage();
            }

            onLoadError(error);
            new AxiosError(error.response).manage();
            return { done: lastPage.value, error: true };
        } finally {
            // A superseded request does not own the flag any more: the fetch of the
            // current filters does, and clearing it here would let a scroll fire a
            // duplicate.
            if (requestGeneration === generation.value) {
                loading.value = false;
            }
        }

        return { done: lastPage.value, error: false };
    }

    /**
     * Silently re-fetch everything currently displayed and swap it in place.
     *
     * Unlike resetList(), this never empties `rows` — the fresh pages are
     * collected first and assigned in a single reactive write, so there is no
     * blank frame, the scroll position holds and the checkbox selection
     * survives. Used for background refreshes (tab regains focus, poll tick,
     * realtime signal) where the user must not notice the reload.
     *
     * Bounded to MAX_REFRESH_PAGES requests so a deeply scrolled list (or one
     * that pulled every page for a grouping) can never fire a silent burst;
     * past that the caller falls back to an explicit resetList().
     *
     * @returns {Promise<boolean>} false when the refresh was skipped or failed
     */
    const MAX_REFRESH_PAGES = 10;

    async function refreshList() {
        if (loading.value || loadingAll.value || refreshing.value) {
            return false;
        }
        if (page.value < 1 || page.value > MAX_REFRESH_PAGES) {
            return false;
        }

        refreshing.value = true;
        const pageCount = page.value;
        const requestGeneration = generation.value;

        try {
            const requests = [];
            for (let p = 1; p <= pageCount; p++) {
                const params = { page: p, page_size: pageSize, per_page: pageSize, ...getQueryParams() };
                if (withCount && p === 1) {
                    params.with_count = true;
                }
                requests.push(api.index(params));
            }

            const responses = await Promise.all(requests);

            // A reset (filter change) beat the refresh home: its pages are the
            // current truth, so this silent one has nothing left to swap in.
            if (requestGeneration !== generation.value) {
                return false;
            }

            const fresh = [];
            let reachedEnd = false;
            for (const response of responses) {
                const data = response.data.data || response.data;
                if (response.data.total !== undefined) {
                    total.value = response.data.total;
                }
                fresh.push(...data);
                reachedEnd = data.length < response.data.per_page;
            }

            rows.value = fresh;
            lastPage.value = reachedEnd;

            const ids = new Set(fresh.map(r => r.id));
            checked.value = checked.value.filter(id => ids.has(id));
            checkedAll.value = checkedAll.value && checked.value.length === fresh.length;

            lastLoadedAt.value = Date.now();

            // Re-arm the infinite-scroll sentinel: it may have completed before
            // the refresh, while the list has since grown past the loaded pages.
            infiniteLoadingId.value = Date.now();
        } catch (error) {
            // A background refresh is best-effort: keep showing the rows we have
            // rather than blanking the list or interrupting with an error toast.
            return false;
        } finally {
            refreshing.value = false;
        }

        return true;
    }

    /**
     * Resolve once no page request is in flight.
     */
    function untilIdle() {
        if (!loading.value) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            const stop = watch(loading, value => {
                if (!value) {
                    stop();
                    resolve();
                }
            });
        });
    }

    /**
     * Handle infinite scroll loading
     * @param {Object} $state - Infinite loading state object
     */
    async function onInfiniteLoad($state) {
        if (lastPage.value) {
            $state.complete();
            return;
        }

        // A page is already in flight — typically a reset that re-armed the
        // sentinel while the previous fetch hadn't answered yet. Returning
        // without resolving $state would leave the sentinel spinning forever
        // (it only re-observes once its state goes back to 'ready'), so wait the
        // request out and hand control back: the observer fires again only if
        // the sentinel is still in view.
        if (loading.value) {
            await untilIdle();
            if (lastPage.value) {
                $state.complete();
            } else {
                $state.loaded();
            }
            return;
        }

        const { done, error } = await fetchNextPage();

        if (error) {
            // A refused page must stop the sentinel. It is still on screen — the
            // list is empty, so nothing pushed it out of view — and `loaded()`
            // would have the observer fire again at once: one refused request
            // becomes an endless stream of them, each with its own error dialog.
            // Changing a filter re-arms the loader through `identifier`.
            $state.error();

            return;
        }

        if (done) {
            $state.complete();
        } else {
            $state.loaded();
        }
    }

    /**
     * Load every remaining page in sequence. Used when a client-side grouping
     * is active: grouping is only correct over the full dataset, so we can't
     * rely on incremental infinite scroll (which reshuffles rows into their
     * groups as pages arrive and leaves trailing groups unreachable).
     * Stops on the first failed request so a transient error can't spin.
     */
    async function loadAllPages() {
        if (loadingAll.value) return;
        loadingAll.value = true;
        try {
            while (!lastPage.value) {
                const { error } = await fetchNextPage();
                if (error) break;
            }
        } finally {
            loadingAll.value = false;
        }
    }

    /**
     * Update a single row in the list without opening a modal.
     * Useful for inline-editing patterns (e.g. tasks).
     * @param {number} id - Row ID to update
     * @param {Object} data - Updated fields to merge
     */
    function updateRow(id, data) {
        const idx = rows.value.findIndex(r => r.id === id);
        if (idx !== -1) {
            rows.value.splice(idx, 1, { ...rows.value[idx], ...data });
        }
    }

    function insertRow(row) {
        rows.value = [row, ...rows.value];
    }

    function removeRow(id) {
        rows.value = rows.value.filter(r => r.id !== id);
    }

    /**
     * Provide callbacks for child components (Preview, etc.)
     * Call this in the parent List component's setup
     */
    function provideParentCallbacks() {
        provide('parentEdit', editItem);
        provide('parentRemove', removeItem);
        provide('parentUpdateRow', updateRow);
        provide('parentInsertRow', insertRow);
        provide('parentRemoveRow', removeRow);
    }

    /**
     * Navigate to item after creation
     * @param {Object} row - Created item
     * @param {string} routeName - Route name for preview
     */
    function navigateToItem(row, routeName) {
        if (router && routeName) {
            router.push({
                name: routeName,
                params: { id: row.id }
            });
        }
    }

    return {
        // State
        rows,
        checked,
        checkedAll,
        selected,
        page,
        lastPage,
        loading,
        total,
        meta,
        infiniteLoadingId,
        lastLoadedAt,
        refreshing,

        // Methods
        resetList,
        refreshList,
        toggleAll,
        addItem,
        editItem,
        removeItem,
        removeSelection: removeSelectionList,
        onInfiniteLoad,
        loadAllPages,
        provideParentCallbacks,
        navigateToItem
    };
}
